import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const QuoteApprovalWorkflow = ({ quote, onClose, onApprovalUpdate }) => {
  const [approvalLevels, setApprovalLevels] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [selectedApprover, setSelectedApprover] = useState('');
  const [isPricingException, setIsPricingException] = useState(quote?.is_pricing_exception || false);
  const [exceptionReason, setExceptionReason] = useState(quote?.exception_reason || '');
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, [quote?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [levelsRes, requestsRes, historyRes, personnelRes] = await Promise.all([
        supabase?.from('quote_approval_levels')?.select('*')?.eq('is_active', true)?.order('level_number'),
        supabase?.from('quote_approval_requests')?.select('*, approver:personnel(*), level:quote_approval_levels(*)')?.eq('quote_id', quote?.id)?.order('level_number'),
        supabase?.from('quote_approval_history')?.select('*')?.eq('quote_id', quote?.id)?.order('created_at', { ascending: false }),
        supabase?.from('personnel')?.select('id, name, email, role')?.order('name')
      ]);
      setApprovalLevels(levelsRes?.data || []);
      setApprovalRequests(requestsRes?.data || []);
      setApprovalHistory(historyRes?.data || []);
      setPersonnel((personnelRes?.data || [])?.map(p => ({ value: p?.id, label: `${p?.name} (${p?.role})`, email: p?.email })));
    } catch (err) {
      console.error('Error fetching approval data:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getRequiredLevel = () => {
    const amount = parseFloat(quote?.total_amount || 0);
    if (isPricingException) return approvalLevels?.find(l => l?.level_number === 3) || approvalLevels?.[approvalLevels?.length - 1];
    return approvalLevels?.find(l => {
      const min = parseFloat(l?.min_amount || 0);
      const max = l?.max_amount ? parseFloat(l?.max_amount) : Infinity;
      return amount >= min && amount < max;
    }) || approvalLevels?.[0];
  };

  const getCurrentRequest = () => approvalRequests?.find(r => r?.status === 'pending');
  const getLatestRequest = () => approvalRequests?.[approvalRequests?.length - 1];

  const handleSubmitForApproval = async () => {
    if (!selectedApprover) { alert('Please select an approver.'); return; }
    try {
      setSubmitting(true);
      const level = getRequiredLevel();
      const approverData = personnel?.find(p => p?.value === selectedApprover);
      const deadline = new Date();
      deadline?.setHours(deadline?.getHours() + (level?.deadline_hours || 48));

      // Mark any existing pending requests as expired
      const pendingIds = approvalRequests?.filter(r => r?.status === 'pending')?.map(r => r?.id);
      if (pendingIds?.length > 0) {
        await supabase?.from('quote_approval_requests')?.update({ status: 'expired' })?.in('id', pendingIds);
      }

      // Create new approval request
      const { error: reqError } = await supabase?.from('quote_approval_requests')?.insert([{
        quote_id: quote?.id,
        level_id: level?.id,
        level_number: level?.level_number || 1,
        approver_id: selectedApprover,
        approver_name: approverData?.label?.split(' (')?.[0],
        approver_email: approverData?.email,
        status: 'pending',
        deadline_at: deadline?.toISOString(),
        is_pricing_exception: isPricingException,
        exception_reason: isPricingException ? exceptionReason : null
      }]);
      if (reqError) throw reqError;

      // Update quote with approval info
      await supabase?.from('quotes')?.update({
        approval_level: level?.level_number || 1,
        approval_deadline: deadline?.toISOString(),
        is_pricing_exception: isPricingException,
        exception_reason: isPricingException ? exceptionReason : null,
        current_approver_id: selectedApprover
      })?.eq('id', quote?.id);

      // Log history
      await supabase?.from('quote_approval_history')?.insert([{
        quote_id: quote?.id,
        action: 'Submitted for Approval',
        actor_name: 'System',
        notes: `Submitted to ${approverData?.label?.split(' (')?.[0]} for Level ${level?.level_number} approval. Deadline: ${deadline?.toLocaleDateString()}`,
        level_number: level?.level_number
      }]);

      // Send email notification
      try {
        await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-quote-notification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}` },
          body: JSON.stringify({
            type: 'approval_requested',
            recipientEmail: approverData?.email,
            recipientName: approverData?.label?.split(' (')?.[0],
            quoteNumber: quote?.quote_number,
            totalAmount: quote?.total_amount,
            deadline: deadline?.toLocaleDateString(),
            levelName: level?.name,
            isPricingException
          })
        });
      } catch (emailErr) { console.error('Email notification failed:', emailErr); }

      setShowSubmitForm(false);
      setSelectedApprover('');
      setDecisionNotes('');
      await fetchData();
      if (onApprovalUpdate) onApprovalUpdate();
    } catch (err) {
      console.error('Error submitting for approval:', err?.message);
      alert('Failed to submit for approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (decision) => {
    const currentReq = getCurrentRequest();
    if (!currentReq) return;
    try {
      setSubmitting(true);
      await supabase?.from('quote_approval_requests')?.update({
        status: decision,
        decision_notes: decisionNotes,
        decided_at: new Date()?.toISOString()
      })?.eq('id', currentReq?.id);

      // Log history
      await supabase?.from('quote_approval_history')?.insert([{
        quote_id: quote?.id,
        approval_request_id: currentReq?.id,
        action: decision === 'approved' ? 'Approved' : 'Rejected',
        actor_name: currentReq?.approver_name || 'Approver',
        notes: decisionNotes || `Quote ${decision} at Level ${currentReq?.level_number}`,
        level_number: currentReq?.level_number
      }]);

      // If approved, check if more levels needed
      if (decision === 'approved') {
        const nextLevel = approvalLevels?.find(l => l?.level_number === currentReq?.level_number + 1);
        if (!nextLevel || !isPricingException) {
          // Final approval — update quote status
          const { data: approvedStatus } = await supabase?.from('quote_statuses')?.select('id')?.eq('name', 'Approved')?.single();
          if (approvedStatus) {
            await supabase?.from('quotes')?.update({ status_id: approvedStatus?.id, approval_date: new Date()?.toISOString() })?.eq('id', quote?.id);
          }
        }
      } else {
        // Rejected — update quote status
        const { data: rejectedStatus } = await supabase?.from('quote_statuses')?.select('id')?.eq('name', 'Rejected')?.single();
        if (rejectedStatus) {
          await supabase?.from('quotes')?.update({ status_id: rejectedStatus?.id })?.eq('id', quote?.id);
        }
      }

      // Send notification
      try {
        await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-quote-notification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}` },
          body: JSON.stringify({
            type: decision === 'approved' ? 'quote_approved' : 'quote_rejected',
            recipientEmail: quote?.salesperson?.email,
            recipientName: quote?.salesperson?.name,
            quoteNumber: quote?.quote_number,
            totalAmount: quote?.total_amount,
            customerName: quote?.customer?.name,
            notes: decisionNotes
          })
        });
      } catch (emailErr) { console.error('Email notification failed:', emailErr); }

      setActiveAction(null);
      setDecisionNotes('');
      await fetchData();
      if (onApprovalUpdate) onApprovalUpdate();
    } catch (err) {
      console.error('Error processing decision:', err?.message);
      alert('Failed to process decision. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalate = async () => {
    const currentReq = getCurrentRequest();
    if (!currentReq) return;
    try {
      setSubmitting(true);
      await supabase?.from('quote_approval_requests')?.update({
        status: 'escalated',
        escalated_at: new Date()?.toISOString(),
        escalation_reason: decisionNotes || 'Escalated due to pricing exception or deadline'
      })?.eq('id', currentReq?.id);

      await supabase?.from('quote_approval_history')?.insert([{
        quote_id: quote?.id,
        approval_request_id: currentReq?.id,
        action: 'Escalated',
        actor_name: 'System',
        notes: decisionNotes || 'Escalated to next approval level',
        level_number: currentReq?.level_number
      }]);

      setActiveAction(null);
      setDecisionNotes('');
      await fetchData();
      if (onApprovalUpdate) onApprovalUpdate();
    } catch (err) {
      console.error('Error escalating:', err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-success/10 text-success border-success/20';
      case 'rejected': return 'bg-error/10 text-error border-error/20';
      case 'escalated': return 'bg-warning/10 text-warning border-warning/20';
      case 'expired': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return 'CheckCircle';
      case 'rejected': return 'XCircle';
      case 'escalated': return 'ArrowUpCircle';
      case 'expired': return 'Clock';
      default: return 'Clock';
    }
  };

  const isDeadlineSoon = (deadline) => {
    if (!deadline) return false;
    const hoursLeft = (new Date(deadline) - new Date()) / (1000 * 60 * 60);
    return hoursLeft > 0 && hoursLeft < 24;
  };

  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'N/A';
    const d = new Date(deadline);
    const hoursLeft = (d - new Date()) / (1000 * 60 * 60);
    if (hoursLeft < 0) return `Expired ${Math.abs(Math.round(hoursLeft))}h ago`;
    if (hoursLeft < 24) return `${Math.round(hoursLeft)}h remaining`;
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const requiredLevel = getRequiredLevel();
  const currentRequest = getCurrentRequest();
  const latestRequest = getLatestRequest();

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2100] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="GitBranch" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Approval Workflow</h2>
              <p className="text-sm text-muted-foreground">{quote?.quote_number} &bull; ${parseFloat(quote?.total_amount || 0)?.toFixed(2)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Loading approval data...</span>
            </div>
          ) : (
            <>
              {/* Required Approval Level */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Info" size={16} color="var(--color-primary)" />
                  <span className="text-xs font-semibold text-primary uppercase tracking-wide">Required Approval Level</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">{requiredLevel?.name || 'Level 1'}</p>
                    <p className="text-sm text-muted-foreground">{requiredLevel?.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">Deadline: {requiredLevel?.deadline_hours}h from submission</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Amount Range</p>
                    <p className="text-sm font-medium text-foreground">
                      ${parseFloat(requiredLevel?.min_amount || 0)?.toLocaleString()} {requiredLevel?.max_amount ? `— $${parseFloat(requiredLevel?.max_amount)?.toLocaleString()}` : '+'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Exception Toggle */}
              <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                    <span className="text-sm font-semibold text-warning">Pricing Exception</span>
                  </div>
                  <button
                    onClick={() => setIsPricingException(!isPricingException)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${isPricingException ? 'bg-warning' : 'bg-muted'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isPricingException ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-2">Mark if this quote contains special pricing that requires executive approval</p>
                {isPricingException && (
                  <Input
                    placeholder="Describe the pricing exception reason..."
                    value={exceptionReason}
                    onChange={(e) => setExceptionReason(e?.target?.value)}
                  />
                )}
              </div>

              {/* Current Pending Request */}
              {currentRequest && (
                <div className={`rounded-lg p-4 border ${isDeadlinePassed(currentRequest?.deadline_at) ? 'bg-error/5 border-error/20' : isDeadlineSoon(currentRequest?.deadline_at) ? 'bg-warning/5 border-warning/20' : 'bg-success/5 border-success/20'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Clock" size={16} color={isDeadlinePassed(currentRequest?.deadline_at) ? 'var(--color-error)' : isDeadlineSoon(currentRequest?.deadline_at) ? 'var(--color-warning)' : 'var(--color-success)'} />
                    <span className="text-sm font-semibold">Pending Approval — Level {currentRequest?.level_number}</span>
                    <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
                      isDeadlinePassed(currentRequest?.deadline_at) ? 'bg-error/10 text-error' : isDeadlineSoon(currentRequest?.deadline_at) ?'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                    }`}>
                      {formatDeadline(currentRequest?.deadline_at)}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">Awaiting: <strong>{currentRequest?.approver_name}</strong></p>
                  {currentRequest?.is_pricing_exception && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-warning">
                      <Icon name="AlertTriangle" size={12} color="currentColor" />
                      <span>Pricing Exception — requires executive review</span>
                    </div>
                  )}

                  {/* Decision Actions */}
                  {activeAction ? (
                    <div className="mt-4 space-y-3">
                      <Input
                        label="Decision Notes"
                        placeholder="Add notes for this decision..."
                        value={decisionNotes}
                        onChange={(e) => setDecisionNotes(e?.target?.value)}
                      />
                      <div className="flex gap-2">
                        {activeAction === 'approve' && (
                          <Button variant="default" size="sm" onClick={() => handleDecision('approved')} loading={submitting} iconName="CheckCircle" iconPosition="left" className="flex-1">Confirm Approve</Button>
                        )}
                        {activeAction === 'reject' && (
                          <Button variant="destructive" size="sm" onClick={() => handleDecision('rejected')} loading={submitting} iconName="XCircle" iconPosition="left" className="flex-1">Confirm Reject</Button>
                        )}
                        {activeAction === 'escalate' && (
                          <Button variant="outline" size="sm" onClick={handleEscalate} loading={submitting} iconName="ArrowUpCircle" iconPosition="left" className="flex-1">Confirm Escalate</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setActiveAction(null); setDecisionNotes(''); }}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-4">
                      <Button variant="default" size="sm" onClick={() => setActiveAction('approve')} iconName="CheckCircle" iconPosition="left">Approve</Button>
                      <Button variant="destructive" size="sm" onClick={() => setActiveAction('reject')} iconName="XCircle" iconPosition="left">Reject</Button>
                      <Button variant="outline" size="sm" onClick={() => setActiveAction('escalate')} iconName="ArrowUpCircle" iconPosition="left">Escalate</Button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit for Approval */}
              {!currentRequest && (
                <div>
                  {!showSubmitForm ? (
                    <Button variant="default" fullWidth iconName="Send" iconPosition="left" onClick={() => setShowSubmitForm(true)}>
                      Submit for Approval
                    </Button>
                  ) : (
                    <div className="border border-border rounded-lg p-4 space-y-4">
                      <div className="flex items-center gap-2">
                        <Icon name="Send" size={16} color="var(--color-primary)" />
                        <span className="text-sm font-semibold text-foreground">Submit for Approval</span>
                      </div>
                      <Select
                        label="Select Approver"
                        placeholder="Choose approver..."
                        options={personnel}
                        value={selectedApprover}
                        onChange={setSelectedApprover}
                      />
                      <div className="flex gap-2">
                        <Button variant="default" size="sm" onClick={handleSubmitForApproval} loading={submitting} iconName="Send" iconPosition="left" className="flex-1">Submit</Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowSubmitForm(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Approval Levels Overview */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">Approval Levels</h3>
                <div className="space-y-2">
                  {approvalLevels?.map((level) => {
                    const req = approvalRequests?.find(r => r?.level_number === level?.level_number);
                    return (
                      <div key={level?.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          req?.status === 'approved' ? 'bg-success text-white' :
                          req?.status === 'rejected' ? 'bg-error text-white' :
                          req?.status === 'pending' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                        }`}>{level?.level_number}</div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{level?.name}</p>
                          <p className="text-xs text-muted-foreground">${parseFloat(level?.min_amount)?.toLocaleString()} {level?.max_amount ? `— $${parseFloat(level?.max_amount)?.toLocaleString()}` : '+'} &bull; {level?.deadline_hours}h deadline</p>
                        </div>
                        {req && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${getStatusColor(req?.status)}`}>
                            {req?.status}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Approval History */}
              {approvalHistory?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Approval History</h3>
                  <div className="space-y-3">
                    {approvalHistory?.map((entry, i) => (
                      <div key={entry?.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name={getStatusIcon(entry?.action?.toLowerCase())} size={14} color="var(--color-primary)" />
                          </div>
                          {i < approvalHistory?.length - 1 && <div className="w-0.5 flex-1 bg-border mt-1" />}
                        </div>
                        <div className="flex-1 pb-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-foreground">{entry?.action}</p>
                            <span className="text-xs text-muted-foreground">{new Date(entry.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{entry?.notes}</p>
                          {entry?.actor_name && <p className="text-xs text-muted-foreground">By {entry?.actor_name}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteApprovalWorkflow;
